/**
 * La etapa Corrección, que corrige de verdad (César, 17-sep: «activo corrector pero no veo resultado»).
 *
 * Se elige qué corregir —un capítulo o el libro— y se manda de una vez al corrector con ese texto.
 * La propuesta sale en el chat de la derecha con UN botón, «Llevar a mi Word», que crea la versión
 * nueva con los cambios marcados. Antes no había texto que corregir si no se abría el documento a
 * mano, y el corrector corregía el mensaje del autor.
 */
import { useEffect, useState } from "react";
import { PuertaPasada, capitulosDeTexto, corregirManuscrito, documento, dolares, mostrarEnCarpeta } from "../api";
import type { Capitulo, ModeloCatalogo, Trabajo } from "../api";
import { estimarCoste, fraseGuionesQuitados, fraseProsaQuitada, frasesSinTocar, nombreDeRuta, palabrasDe } from "../logica";
import type { SinTocar } from "../logica";
import { ListaDePasos } from "./ListaDePasos";
import { useFlujo } from "../flujo";

export default function Corregir({ doc, carpeta, soportado, trabajo, ultimaHecha = null, alLanzar, precio, alCorregir, alHablar, corrigiendo = false }: {
  doc: string | null;
  carpeta: string;
  soportado: boolean;           // el motor corrige el manuscrito entero (1.6.3)
  trabajo: Trabajo | null;
  ultimaHecha?: Trabajo | null;   // la última corrección terminada, aunque después se haya lanzado otra
  alLanzar: () => void;
  precio: ModeloCatalogo | undefined;
  alCorregir?: (capitulo: { title: string; texto: string } | null) => void;
  /** El corrector está trabajando en lo que se le mandó desde aquí (26-sep: parecía que no hacía nada). */
  corrigiendo?: boolean;
  alHablar: () => void;
}) {
  const [caps, setCaps] = useState<Capitulo[] | null>(null);
  const [motivoCaps, setMotivoCaps] = useState<string | null>(null);   // 17-sep: capítulos que no valen
  const [total, setTotal] = useState<number | null>(null);
  const [cual, setCual] = useState<string>("");   // "" = el primero; "todo" = el libro
  const [ocupado, setOcupado] = useState(false);
  const [puerta, setPuerta] = useState<string | null>(null);
  const [soloCapitulo, setSoloCapitulo] = useState(false);
  const flujo = useFlujo();   // el Studio nuevo: sin corrector con quien hablar; se corrige el libro entero
  const [mandado, setMandado] = useState<string | null>(null);   // lo último que se mandó al corrector desde aquí
  const costeTodoTexto = (() => { const c = total != null ? estimarCoste(total, precio, undefined, "reescritura").coste : null; return c != null ? dolares(c) : null; })();

  // Rais, 24-sep: terminó la corrección, el Studio pasó solo a la v10 y la pantalla volvió a ofrecer
  // «Corregir el manuscrito entero»; lo pulsó creyendo que faltaba la comprobación y se volvió a
  // cobrar el libro entero. Si el documento abierto ES la versión que salió de una corrección, se
  // dice, y corregirlo otra vez pide confirmación con su coste.
  const salidaHecha = (ultimaHecha?.resultado as { build?: string } | null)?.build ?? null;
  const yaCorregido = !!doc && !!salidaHecha && nombreDeRuta(doc) === salidaHecha;

  async function corregirTodo() {
    if (!doc) return;
    if (yaCorregido && !window.confirm(`«${salidaHecha}» ya es la versión corregida. Corregirla otra vez vuelve a leer el libro entero y se cobra de nuevo${costeTodoTexto ? ` (~${costeTodoTexto})` : ""}. Lo que te queda es revisarla en Word, que no cuesta nada.\n\n¿Corregirla otra vez de todos modos?`)) return;
    setOcupado(true); setPuerta(null);
    try { await corregirManuscrito(carpeta, doc); alLanzar(); }
    catch (e) { setPuerta(e instanceof PuertaPasada ? e.message : (e as Error).message); }
    finally { setOcupado(false); }
  }

  useEffect(() => {
    setCaps(null); setTotal(null); setMotivoCaps(null);
    if (!doc) return;
    documento(doc).then(async (d) => {
      if ("error" in d) return;
      setTotal(palabrasDe(d.text));
      const r = await capitulosDeTexto(d.text, carpeta, doc).catch(() => null);
      setCaps(r && r.fiable !== false ? r.capitulos.filter((c) => c.texto.trim()) : []);
      setMotivoCaps(r && r.fiable === false ? r.motivo ?? "no he encontrado capítulos fiables" : null);
    }).catch(() => {});
  }, [doc]);

  if (!doc) {
    return (
      <section className="carta">
        <h2>Corregir</h2>
        <p className="vacio">Elige antes tu manuscrito en Proyectos: {flujo ? "hace falta el texto que hay que corregir" : "el corrector necesita el texto que tiene que corregir"}.</p>
      </section>
    );
  }

  // CON MANUSCRITOS ENTEROS (Rais, 17-sep): lo primero es corregir el libro completo, en segundo plano,
  // en una versión nueva con los cambios marcados. Un capítulo suelto queda como opción secundaria.
  if (soportado && !soloCapitulo) {
    const enCurso = trabajo?.estado === "en_curso";
    const fuente = trabajo?.estado === "hecho" ? trabajo : !enCurso ? ultimaHecha : null;
    const hecho = (fuente?.resultado ?? null) as { build?: string; aplicadas?: number; sin_ancla?: number; huellas?: { antes: number; despues: number } | null;
      para_ti?: number; informe_para_ti?: string | null; sin_tocar?: SinTocar[]; prosa_quitada?: { quitados?: number; devueltos?: number }; guiones_quitados?: number } | null;
    const costeTodo = total != null ? estimarCoste(total, precio, undefined, "reescritura").coste : null;
    // CON LA CORRECCIÓN HECHA Y ABIERTA, LA TARJETA DE «CORREGIR» SE RECOGE (César, 4-oct: «te anuncia
    // corrección hecha y debajo mantiene el cuadro para activar la corrección, que en realidad ya has
    // hecho»). Corregirla otra vez sigue a mano, pero como lo que es: una línea al pie, no una invitación.
    const recogida = !!hecho && yaCorregido && !enCurso && !puerta && trabajo?.estado !== "fallido" && trabajo?.estado !== "interrumpido";
    return (
      <>
        {hecho && (
          <section className="carta">
            <h2>Corrección hecha<span className="sub">{hecho.aplicadas} cambios</span></h2>
            <p className="vacio">Se ha creado <b>{hecho.build}</b> con los cambios marcados. Tu documento anterior sigue intacto.</p>
            {/* «Lo que te dejo a ti» (25-sep, la skill del corrector): lo que no era suyo decidir, en un Word aparte. */}
            {hecho.informe_para_ti && (
              <p className="vacio">
                {hecho.para_ti ? <><b>Te deja {hecho.para_ti} {hecho.para_ti === 1 ? "decisión" : "decisiones"} a ti</b> (dudas de estilo, de hecho o de nombres que no ha querido tocar): </>
                  : <>No ha quedado ninguna decisión que sea tuya. </>}
                <span className="mono">{nombreDeRuta(hecho.informe_para_ti)}</span>{" "}
                <button className="btn texto" onClick={() => mostrarEnCarpeta(hecho.informe_para_ti!)}>Enseñar en la carpeta</button>
              </p>
            )}
            {hecho.huellas && <p className="vacio">Humanizador: {hecho.huellas.antes === 0 ? "no había huellas de IA de catálogo; se revisó igual" : `${hecho.huellas.antes} huellas de IA de catálogo en tu texto, ${hecho.huellas.despues} en lo que propuso el corrector${hecho.sin_ancla ? " (los cambios que no encontraron su sitio no se aplicaron: alguna puede seguir en la versión)" : ""}`}. Las marcas del revisor dicen «corrección y humanizador».</p>}
            {!!hecho.sin_ancla && <p className="vacio" style={{ color: "var(--acc)" }}>{hecho.sin_ancla} cambios propuestos no encontraron su sitio y no se aplicaron.</p>}
            {frasesSinTocar(hecho.sin_tocar) && <p className="vacio" style={{ color: "var(--acc)" }}>{frasesSinTocar(hecho.sin_tocar)}</p>}
            {fraseProsaQuitada(hecho.prosa_quitada) && <p className="vacio">{fraseProsaQuitada(hecho.prosa_quitada)}</p>}
            {fraseGuionesQuitados(hecho.guiones_quitados) && <p className="vacio">{fraseGuionesQuitados(hecho.guiones_quitados)}</p>}
            {yaCorregido && <p className="vacio"><b>Es la versión que tienes abierta.</b> La comprobación ya está hecha: el motor verificó que cada cambio se puede aceptar o rechazar antes de escribirla. No hace falta corregirla otra vez; lo que falta es tu revisión en Word, que no cuesta nada.</p>}
            <ol className="pasos">
              <li>Abre <b>{hecho.build}</b> en Word (está en la carpeta del proyecto).</li>
              <li>Revisar → Aceptar o Rechazar los cambios.</li>
              <li>Guarda: esa versión pasa a ser tu manuscrito.</li>
            </ol>
            {recogida && (
              <div className="botones">
                <button className="btn texto" disabled={ocupado || total == null} onClick={corregirTodo}>Corregirla otra vez{costeTodoTexto ? ` · ~${costeTodoTexto}` : ""}</button>
                <button className="btn texto" onClick={alHablar}>{flujo ? "Preguntar al coach" : "Hablarlo con el corrector"}</button>
              </div>
            )}
          </section>
        )}
        {!recogida && <section className="carta">
          <h2>Corregir el manuscrito<span className="sub">{nombreDeRuta(doc)}</span></h2>
          <p className="vacio">
            Línea, estilo y ortotipografía de todo el libro, y el <b>humanizador</b>: quita el IA-slop (muletillas de ensayo,
            paralelismos de plantilla, verbos comodín, moralejas) sin tocar tu voz. {flujo ? "Se trabaja" : "El corrector lo trabaja"} por partes en tu ordenador —puedes
            cerrar el Studio— y al terminar crea una versión nueva con todos los cambios marcados. Tu documento no se toca.
          </p>
          {enCurso ? (
            <p className="vacio">Corrigiendo{trabajo?.progreso ? `: parte ${Math.min(trabajo.progreso.hecho + 1, trabajo.progreso.total)} de ${trabajo.progreso.total}` : "…"}.</p>
          ) : (
            <div className="pie-form">
              <p className="vacio">{total != null ? `~${total.toLocaleString("es-ES")} palabras` : "Leyendo el manuscrito…"}{costeTodo != null ? ` · ~${dolares(costeTodo)} de tu llave` : ""}</p>
              <button className={yaCorregido ? "btn texto" : "btn"} disabled={ocupado || total == null} onClick={corregirTodo}>{yaCorregido ? "Corregir esta versión otra vez" : "Corregir el manuscrito entero"}</button>
            </div>
          )}
          {trabajo && trabajo.estado !== "hecho" && <ListaDePasos trabajo={trabajo} />}
          {puerta && <div className="aviso">{puerta}</div>}
          {trabajo?.estado === "interrumpido" && <p className="vacio" style={{ color: "var(--acc)" }}>La última corrección se cortó en la parte {Math.min((trabajo.progreso?.hecho ?? 0) + 1, trabajo.progreso?.total ?? 1)} de {trabajo.progreso?.total ?? "?"}: el motor se cerró o se reinició mientras trabajaba. No se ha creado ninguna versión. Lo ya corregido queda guardado: si relanzas con la misma versión del Studio, no se vuelve a cobrar.</p>}
          {trabajo?.estado === "fallido" && <p className="vacio" style={{ color: "var(--acc)" }}>La última corrección no se completó: {trabajo.error} No se ha creado ninguna versión.</p>}
          <div className="botones">
            {!flujo && <button className="btn texto" onClick={() => setSoloCapitulo(true)}>Corregir solo un capítulo</button>}
            <button className="btn texto" onClick={alHablar}>{flujo ? "Preguntar antes al coach" : "Hablarlo antes con el corrector"}</button>
          </div>
        </section>}
      </>
    );
  }

  const elegido = cual === "todo" ? null : caps?.[Number(cual || 0)] ?? null;
  const palabras = cual === "todo" ? total : elegido?.palabras ?? null;
  const coste = palabras != null ? estimarCoste(palabras, precio, undefined, "reescritura", { unaLlamada: cual !== "todo" }).coste : null;
  const largo = cual === "todo" && (total ?? 0) > 15000;

  return (
    <section className="carta">
      <h2>Corregir<span className="sub">{nombreDeRuta(doc)}</span></h2>
      <p className="vacio">
        Línea, estilo y ortotipografía, y el <b>humanizador</b>, que quita el IA-slop sin tocar tu voz. El corrector te devuelve el texto corregido en el chat de la derecha; lo
        llevas a tu Word con un botón y se crea una versión nueva con los cambios marcados. Tu documento no se toca.
      </p>
      <div className="formulario">
        <label className="campo">
          <span>Qué corregir</span>
          <select className="escribir" value={cual} onChange={(e) => setCual(e.target.value)} disabled={!caps}>
            {!caps && <option>Leyendo el manuscrito…</option>}
            {caps?.map((c, i) => <option key={i} value={String(i)}>{c.title} · {c.palabras.toLocaleString("es-ES")} palabras</option>)}
            {caps && <option value="todo">Todo el libro · {(total ?? 0).toLocaleString("es-ES")} palabras</option>}
          </select>
        </label>
        {motivoCaps && <p className="vacio">Se corregirá el libro entero: {motivoCaps}, así que no puedo ofrecerte capítulos.</p>}
        {largo && !motivoCaps && <p className="vacio">Un libro entero de una vez es lento y caro: mejor capítulo a capítulo.</p>}
        <div className="pie-form">
          <p className="vacio">{palabras != null ? `~${palabras.toLocaleString("es-ES")} palabras` : ""}{coste != null ? ` · ~${dolares(coste)} de tu llave, razonamiento incluido` : ""}</p>
          <button className="btn" disabled={!caps || !alCorregir || corrigiendo} onClick={() => { setMandado(elegido?.title ?? "el libro"); alCorregir?.(elegido ? { title: elegido.title, texto: elegido.texto } : null); }}>
            {corrigiendo ? "Corrigiendo…" : elegido ? `Corregir ${elegido.title}` : "Corregir el libro"}
          </button>
        </div>
        {/* Lo que pasa después de pulsar, dicho (Rais, 26-sep: «aprietas el botón y se queda igual»). */}
        {corrigiendo && (
          <div className="aviso"><span>
            El corrector está trabajando en <b>{mandado ?? "tu texto"}</b>. Suele tardar unos minutos. El texto corregido te llega
            al chat del Corrector, a la derecha, con el botón para llevarlo a tu Word.
          </span></div>
        )}
        {!corrigiendo && mandado && (
          <p className="vacio"><span>Listo: <b>{mandado}</b> corregido está en el chat del Corrector, a la derecha.</span></p>
        )}
        <div className="botones"><button className="btn texto" onClick={alHablar}>Prefiero hablarlo antes con el corrector</button></div>
      </div>
    </section>
  );
}
